import React, { useMemo } from "react";
import {
  Phone, Mail, FileText, CreditCard, ShoppingCart, CheckSquare,
  LifeBuoy, DollarSign, Wallet, Repeat, PieChart, Calculator,
  GitBranch, StickyNote, Calendar, Bell, Upload, Download, LayoutDashboard,
  Users, LogOut
} from "lucide-react";
import { CATEGORIES } from "../lib/constants";
import { useAuth } from "../lib/auth";

const ICONS = {
  Phone, Mail, FileText, CreditCard, ShoppingCart, CheckSquare,
  LifeBuoy, DollarSign, Wallet, Repeat,
};

const CatIcon = ({ name, size = 19 }) => {
  const Cmp = ICONS[name] || CheckSquare;
  return <Cmp size={size} strokeWidth={1.75} />;
};

const NavBtn = ({ active, onClick, icon, label, count, badge, testId, altBorder }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between p-3 md:px-6 transition-colors ${
      active
        ? `bg-white text-ink shadow-sm border-r-4 ${altBorder || "border-blue-500"}`
        : "text-slate-500 hover:bg-white hover:text-ink"
    }`}
    data-testid={testId}
  >
    <div className="flex items-center">
      {icon}
      <span className="ml-3 hidden md:block font-medium text-sm">{label}</span>
    </div>
    <div className="hidden md:flex items-center gap-1">
      {badge}
      {count !== undefined && (
        <span className={`flex items-center justify-center text-xs font-bold h-6 min-w-[1.5rem] px-2 rounded-full shadow-sm bg-blue-600 text-white`}>
          {count}
        </span>
      )}
    </div>
  </button>
);

export default function Sidebar({
  currentView, setCurrentView, activeTab, setActiveTab,
  tasks, postIts, recurringTasks, recurringAlerts, logo, setLogo,
  importData, exportData,
}) {
  const { user, logout } = useAuth();

  const categoryCounts = useMemo(() => {
    const counts = {};
    Object.keys(CATEGORIES).forEach((k) => (counts[k] = { active: 0, completed: 0 }));
    tasks.forEach((t) => {
      if (counts[t.category]) {
        if (t.completed) counts[t.category].completed++;
        else counts[t.category].active++;
      }
    });
    return counts;
  }, [tasks]);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setLogo(reader.result);
    reader.readAsDataURL(file);
  };

  const sortedCategories = useMemo(
    () => Object.values(CATEGORIES).sort((a, b) => a.label.localeCompare(b.label)),
    []
  );

  return (
    <aside className="w-20 md:w-64 bg-cream text-ink flex flex-col flex-shrink-0 z-20 border-r border-ink/10" data-testid="sidebar">
      <div className="p-4 flex items-center border-b border-ink/10 h-20 relative">
        <label className="flex items-center cursor-pointer w-full h-full" title="Carica logo">
          <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="logo-upload-input" />
          <div className="w-full h-full flex items-center gap-3">
            {logo ? (
              <img src={logo} alt="Logo" className="h-10 max-w-[44%] object-contain" onError={() => setLogo(null)} />
            ) : (
              <div className="bg-blue-600 p-2 rounded-lg shrink-0">
                <LayoutDashboard size={22} strokeWidth={2} className="text-white" />
              </div>
            )}
            <span className="hidden md:block font-outfit text-lg leading-tight tracking-tight">
              IMTask <span className="text-blue-600 font-bold">PRO</span>
            </span>
          </div>
        </label>
      </div>

      {/* Nota: pulsante "Assistente AI" rimosso. La chat AI è ora un widget
          flottante globale (AdminAIChatWidget) disponibile su ogni pagina. */}

      <nav className="flex-1 overflow-y-auto py-3 custom-scrollbar">
        <NavBtn
          active={currentView === "CRM" && activeTab === "DASHBOARD"}
          onClick={() => { setCurrentView("CRM"); setActiveTab("DASHBOARD"); }}
          icon={<PieChart size={20} strokeWidth={1.75} />}
          label="Dashboard"
          testId="nav-dashboard"
        />
        <NavBtn
          active={currentView === "CLIENTS"}
          onClick={() => setCurrentView("CLIENTS")}
          icon={<Users size={20} strokeWidth={1.75} />}
          label="Rubrica Clienti"
          testId="nav-clients"
        />

        <div className="mt-4 mb-2 px-6 text-[10px] font-semibold text-slate-500 uppercase hidden md:block tracking-wider">
          Categorie
        </div>

        {sortedCategories.map((cat) => {
          const active = activeTab === cat.id && currentView === "CRM";
          return (
            <button
              key={cat.id}
              onClick={() => { setCurrentView("CRM"); setActiveTab(cat.id); }}
              className={`w-full flex items-center justify-between p-3 md:px-6 transition-colors ${
                active ? `bg-white text-ink shadow-sm border-r-4 ${cat.border}` : "text-slate-500 hover:bg-white hover:text-ink"
              }`}
              data-testid={`nav-cat-${cat.id}`}
            >
              <div className="flex items-center">
                <CatIcon name={cat.icon} size={19} />
                <span className="ml-3 hidden md:block font-medium text-sm">{cat.label}</span>
              </div>
              <div className="hidden md:flex items-center gap-1">
                <span className="flex items-center justify-center bg-blue-600 text-white text-xs font-bold h-6 min-w-[1.5rem] px-2 rounded-full shadow-sm">
                  {categoryCounts[cat.id]?.active || 0}
                </span>
                {categoryCounts[cat.id]?.completed > 0 && (
                  <span className="flex items-center justify-center bg-slate-200 text-slate-600 text-xs font-bold px-2 py-0.5 rounded-full min-w-[1.5rem]" title="Completate">
                    {categoryCounts[cat.id].completed}
                  </span>
                )}
              </div>
            </button>
          );
        })}

        <div className="mt-6 mb-2 px-6 text-[10px] font-semibold text-slate-500 uppercase hidden md:block border-t border-ink/10 pt-4 tracking-wider">
          Strumenti
        </div>

        <NavBtn
          active={currentView === "CALCULATOR"}
          onClick={() => setCurrentView("CALCULATOR")}
          icon={<Calculator size={20} strokeWidth={1.75} />}
          label="Calcolatore Prezzo"
          testId="nav-calculator"
        />
        <NavBtn
          active={currentView === "SCENARIOS"}
          onClick={() => setCurrentView("SCENARIOS")}
          icon={<GitBranch size={20} strokeWidth={1.75} />}
          label="Scenari Pagamento"
          testId="nav-scenarios"
        />
        <NavBtn
          active={currentView === "POSTITS"}
          onClick={() => setCurrentView("POSTITS")}
          icon={<StickyNote size={20} strokeWidth={1.75} />}
          label="Post-it Note"
          count={postIts.length}
          testId="nav-postits"
        />
        <NavBtn
          active={currentView === "RECURRING"}
          onClick={() => setCurrentView("RECURRING")}
          icon={
            <div className="relative">
              <Calendar size={20} strokeWidth={1.75} />
              {recurringAlerts.length > 0 && <span className="absolute -top-2 -right-2 w-2.5 h-2.5 bg-red-500 rounded-full animate-ping"></span>}
            </div>
          }
          label="Scadenziario"
          count={recurringTasks.length}
          badge={recurringAlerts.length > 0 ? <div className="animate-pulse text-red-400"><Bell size={15} /></div> : null}
          testId="nav-recurring"
        />
      </nav>

      {/* User & logout */}
      <div className="p-3 border-t border-ink/10 space-y-2">
        <div className="hidden md:flex items-center gap-2 p-2 rounded-lg bg-white border border-ink/10" data-testid="sidebar-user">
          <div className="w-8 h-8 rounded-full bg-blue-700 flex items-center justify-center text-white text-xs font-bold shrink-0">
            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-ink truncate">{user?.name || user?.email}</div>
            <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
          </div>
        </div>
        <div className="flex items-center justify-around md:justify-between gap-2">
          <label className="flex items-center text-slate-500 hover:text-ink cursor-pointer transition text-xs" title="Importa backup JSON">
            <Upload size={16} />
            <span className="ml-1.5 hidden md:inline">Importa</span>
            <input type="file" accept=".json" onChange={importData} className="hidden" data-testid="import-json-input" />
          </label>
          <button onClick={exportData} className="flex items-center text-slate-500 hover:text-ink transition text-xs" title="Esporta backup" data-testid="export-json-btn">
            <Download size={16} />
            <span className="ml-1.5 hidden md:inline">Esporta</span>
          </button>
          <button onClick={logout} className="flex items-center text-slate-500 hover:text-red-600 transition text-xs" title="Esci" data-testid="logout-btn">
            <LogOut size={16} />
            <span className="ml-1.5 hidden md:inline">Esci</span>
          </button>
        </div>
      </div>
    </aside>
  );
}
